import { useCallback, useEffect, useRef } from 'react';
import type { IPoint } from './useLongPress';

/** Radius of the clear spot under the finger, in screen pixels (roughly a fingertip) */
const INK_RADIUS_PX = 32;

/** How long a spot stays clear before it has fully blurred back over */
const INK_LIFETIME_MS = 2200;

/** The share of a spot's lifetime it stays full size before shrinking away */
const INK_HOLD_SHARE = 0.4;

/** Where a spot's edge starts feathering, as a share of its radius */
const INK_FEATHER_START = '50%';

/**
 * A finger that moves less than this share of the radius refreshes its spot instead of adding
 * one, so holding still keeps the spot clear without piling up mask layers.
 */
const INK_SPACING_SHARE = 1 / 3;

/** Spots that have shrunk below this are left out of the mask */
const MIN_DRAWN_RADIUS_PX = 0.5;

/** Oldest spots are dropped past this, bounding the number of mask layers */
const MAX_INK_POINTS = 60;

/**
 * The preview demo's pretend finger: how long it holds still before inking (longer than the real
 * long press, so the hold reads as a step), how long it draws, then how long it rests.
 */
const DEMO_HOLD_MS = 700;
const DEMO_STROKE_MS = 2400;
// Outlasts INK_LIFETIME_MS, so the last stroke's trail is gone before the next hold
const DEMO_REST_MS = INK_LIFETIME_MS + 200;
const DEMO_CYCLE_MS = DEMO_HOLD_MS + DEMO_STROKE_MS + DEMO_REST_MS;

/** How far out the hold ring starts, relative to the marker; it closes in to the marker's edge */
const DEMO_HOLD_RING_START_SCALE = 1.8;

/** Diameter of the demo's touch marker, in screen pixels: a little smaller than the ink spot */
const DEMO_FINGER_PX = 40;

/** Caps the touch marker at this share of the photo's shorter side, for small grid thumbnails */
const DEMO_FINGER_MAX_SHARE = 0.35;

/** How many spots a reduced-motion demo's still stroke is drawn with */
const DEMO_STILL_SAMPLES = 24;

/**
 * Where the demo's finger is partway through a stroke (0 to 1), as a share of the layer's
 * width and height: one loop of a figure eight around the center.
 */
const demoPointAt = (progress: number): IPoint => {
    const angle = progress * 2 * Math.PI;

    return { x: 0.5 + 0.28 * Math.sin(angle), y: 0.5 + 0.18 * Math.sin(2 * angle) };
};

const prefersReducedMotion = () =>
    typeof window.matchMedia === 'function' &&
    window.matchMedia('(prefers-reduced-motion: reduce)').matches;

interface IInkPoint extends IPoint {
    /** Radius in the layer's own (untransformed) pixels */
    radius: number;
    time: number;
}

const supportsStandardMaskComposite = () =>
    typeof CSS !== 'undefined' && !!CSS.supports?.('mask-composite', 'intersect');

/**
 * Each spot is a mask layer that's opaque everywhere except a hole at the spot. Intersecting
 * the layers leaves the union of the holes clear, so the blurred layer shows the sharp photo
 * (or, for a backdrop, the unblurred slide) through every live spot.
 */
const applyMask = (layer: HTMLElement, gradients: string[]) => {
    const image = gradients.join(', ');

    if (supportsStandardMaskComposite()) {
        layer.style.maskImage = image;
        layer.style.maskComposite = gradients.length ? 'intersect' : '';
    } else {
        layer.style.webkitMaskImage = image;
        // Safari's legacy name for intersect
        layer.style.setProperty('-webkit-mask-composite', gradients.length ? 'source-in' : '');
    }
};

const radiusAt = (point: IInkPoint, now: number) => {
    const age = now - point.time;
    const holdMs = INK_LIFETIME_MS * INK_HOLD_SHARE;

    return age <= holdMs
        ? point.radius
        : point.radius * Math.max(0, 1 - (age - holdMs) / (INK_LIFETIME_MS - holdMs));
};

/**
 * Magic Reveal: a fully blurred layer that the finger wipes clear in a trail that fades back to
 * blur. Attach `inkLayerRef` to the blurred layer and feed `addInkPoint` the finger's
 * viewport coordinates. The mask is written straight to the layer each animation frame, so the
 * trail doesn't re-render the photo's component.
 *
 * With isDemo (the photo a preview demonstrates on, since nobody can touch it), a pretend finger draws strokes on its own instead,
 * shown by the element on `demoFingerRef`: it rests on the photo while a ring closes in (the
 * press and hold), then inks. Under reduced motion it holds one still stroke rather
 * than moving.
 */
const useMagicReveal = (isDemo = false) => {
    const layerRef = useRef<Nullable<HTMLElement>>(null);
    const fingerRef = useRef<Nullable<HTMLElement>>(null);
    const pointsRef = useRef<IInkPoint[]>([]);
    const frameRef = useRef(0);

    const draw = useCallback(() => {
        frameRef.current = 0;

        const layer = layerRef.current;
        const now = Date.now();

        pointsRef.current = pointsRef.current.filter((point) => now - point.time < INK_LIFETIME_MS);

        if (!layer) {
            return;
        }

        applyMask(
            layer,
            pointsRef.current
                .map((point) => ({ ...point, radius: radiusAt(point, now) }))
                // A zero-size gradient would invalidate the whole mask, clearing every spot
                .filter(({ radius }) => radius >= MIN_DRAWN_RADIUS_PX)
                .map(
                    ({ radius, x, y }) =>
                        `radial-gradient(circle ${radius}px at ${x}px ${y}px, transparent ${INK_FEATHER_START}, #000 100%)`
                )
        );

        if (pointsRef.current.length) {
            frameRef.current = requestAnimationFrame(draw);
        }
    }, []);

    useEffect(() => () => cancelAnimationFrame(frameRef.current), []);

    const addInkPoint = useCallback(
        (clientPoint: Nullable<IPoint>) => {
            const layer = layerRef.current;

            if (!layer || !clientPoint) {
                return;
            }

            // Measure against the layer's on-screen box, which includes any zoom transform, and
            // convert back to its own pixels, which is what the mask is laid out in.
            const rect = layer.getBoundingClientRect();
            const scale = layer.offsetWidth ? rect.width / layer.offsetWidth : 1;
            const point: IInkPoint = {
                x: (clientPoint.x - rect.left) / scale,
                y: (clientPoint.y - rect.top) / scale,
                radius: INK_RADIUS_PX / scale,
                time: Date.now()
            };
            const points = pointsRef.current;
            const last = points[points.length - 1];

            if (
                last &&
                Math.hypot(point.x - last.x, point.y - last.y) < last.radius * INK_SPACING_SHARE
            ) {
                last.time = point.time;
            } else {
                points.push(point);
                pointsRef.current = points.slice(-MAX_INK_POINTS);
            }

            if (!frameRef.current) {
                frameRef.current = requestAnimationFrame(draw);
            }
        },
        [draw]
    );

    useEffect(() => {
        if (!isDemo) {
            return undefined;
        }

        // Offset each photo's strokes so a grid of them doesn't move in lockstep
        const startedAt = Date.now() - Math.random() * DEMO_CYCLE_MS;

        /** Where the finger is on screen partway through a stroke */
        const clientPointAt = (progress: number) => {
            const layer = layerRef.current;

            if (!layer) {
                return null;
            }

            const rect = layer.getBoundingClientRect();
            const { x, y } = demoPointAt(progress);

            return { x: rect.left + x * rect.width, y: rect.top + y * rect.height };
        };

        const inkAt = (progress: number) => {
            const clientPoint = clientPointAt(progress);

            addInkPoint(clientPoint);

            return clientPoint;
        };

        /**
         * Centers the touch marker on the finger, or hides it while the finger is lifted. While
         * the finger is holding, its ring closes in by holdProgress (0 to 1); otherwise the ring
         * is hidden.
         */
        const placeFinger = (clientPoint: Nullable<IPoint>, holdProgress?: number) => {
            const finger = fingerRef.current;
            const parent = finger?.parentElement;
            const ring = finger?.firstElementChild;

            if (!finger || !parent) {
                return;
            }

            finger.style.opacity = clientPoint ? '1' : '0';

            if (ring instanceof HTMLElement) {
                const isHolding = holdProgress !== undefined;
                const scale =
                    DEMO_HOLD_RING_START_SCALE -
                    (DEMO_HOLD_RING_START_SCALE - 1) * (holdProgress ?? 1);

                ring.style.opacity = isHolding ? String(holdProgress) : '0';
                ring.style.transform = `scale(${scale})`;
            }

            if (clientPoint) {
                // The marker sits in the photo's positioned parent; place it in that parent's
                // own pixels, undoing any zoom or preview scaling.
                const rect = parent.getBoundingClientRect();
                const scale = parent.offsetWidth ? rect.width / parent.offsetWidth : 1;
                const size = Math.min(
                    DEMO_FINGER_PX / scale,
                    DEMO_FINGER_MAX_SHARE * Math.min(parent.offsetWidth, parent.offsetHeight) ||
                        Infinity
                );
                const left = (clientPoint.x - rect.left) / scale - size / 2;
                const top = (clientPoint.y - rect.top) / scale - size / 2;

                finger.style.width = `${size}px`;
                finger.style.height = `${size}px`;
                finger.style.transform = `translate(${left}px, ${top}px)`;
            }
        };

        if (prefersReducedMotion()) {
            // Re-inking the same half stroke before its spots start shrinking keeps it clear
            // without anything moving
            const inkStill = () => {
                let lastPoint: Nullable<IPoint> = null;

                for (let sample = 0; sample <= DEMO_STILL_SAMPLES; sample++) {
                    lastPoint = inkAt(sample / DEMO_STILL_SAMPLES / 2);
                }

                placeFinger(lastPoint);
            };

            const interval = setInterval(inkStill, (INK_LIFETIME_MS * INK_HOLD_SHARE) / 2);

            inkStill();

            return () => clearInterval(interval);
        }

        let frame = 0;

        const tick = () => {
            const elapsed = (Date.now() - startedAt) % DEMO_CYCLE_MS;
            const strokeElapsed = elapsed - DEMO_HOLD_MS;

            if (strokeElapsed < 0) {
                // Holding: the finger is down but nothing is revealed until the hold completes
                placeFinger(clientPointAt(0), elapsed / DEMO_HOLD_MS);
            } else {
                placeFinger(
                    strokeElapsed < DEMO_STROKE_MS ? inkAt(strokeElapsed / DEMO_STROKE_MS) : null
                );
            }

            frame = requestAnimationFrame(tick);
        };

        frame = requestAnimationFrame(tick);

        return () => cancelAnimationFrame(frame);
    }, [isDemo, addInkPoint]);

    const inkLayerRef = useCallback((layer: Nullable<HTMLElement>) => {
        // A replaced layer is a different photo (e.g. the lightbox moved on); the old trail
        // doesn't belong on it
        if (layer !== layerRef.current) {
            pointsRef.current = [];
            cancelAnimationFrame(frameRef.current);
            frameRef.current = 0;
        }

        layerRef.current = layer;
    }, []);

    const demoFingerRef = useCallback((finger: Nullable<HTMLElement>) => {
        fingerRef.current = finger;
    }, []);

    return { inkLayerRef, addInkPoint, demoFingerRef: isDemo ? demoFingerRef : undefined };
};

export default useMagicReveal;
